import React, { FC } from 'react';
import dayjs from 'dayjs';
import { Controller, UseFormReturn } from 'react-hook-form';
import { noop } from 'lodash/fp';
import { FormSpacer, InformationPopUp } from 'components';
import { BreachType, FormDataType, gicopAreaList } from 'contexts/FormProvider';
import { useContentContext } from '@qbe/dfc-content';
import { Button, FormGroupLabel, DatePicker, Box } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { DropdownController, TextFieldController } from 'controllers';
import DataPrivacy from '../components/DataPrivacy';
import { TrackEvent, formatNext } from 'hooks/useAnalytics';
import { useContentComplaintPartners } from 'utils/content';

interface Props {
  form: UseFormReturn<FormDataType>;
  isFieldRequired: (fieldName: keyof FormDataType) => boolean;
  isValid: boolean;
  showNextButton: boolean;
  onSubmit: () => void;
}

const BreachDetails: FC<Props> = ({
  form,
  isFieldRequired,
  isValid = false,
  showNextButton = true,
  onSubmit = noop,
}) => {
  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });
  const { watch, control, getValues } = form;
  const breachType = watch('breachType') as BreachType;
  const isGicop = breachType === 'GI Code of Practice';
  const isTMD = breachType === 'Dealings Outside of TMD';
  const isDataPrivacy = breachType === 'Data, Privacy, Security';

  const handleClick = () => {
    TrackEvent(formatNext('nextRootCause'), getValues());
    onSubmit();
  };

  return (
    <FormCard variant="sectionTitle" title={getTextContent('breach.tell.us.more.breachDetails.heading')}>
      <>
        <FormGroupLabel
          alignCenter
          required
          legendElementOveride
          title={getTextContent('breach.tell.us.more.breachDetails.dateOccured')}
          titleSuffix={
            <InformationPopUp
              message={getTextContent('breach.tell.us.more.breachDetails.dateOccured.infoText')}
              dataTrack="infoIconAcknowledgement"
            />
          }
        />
        <FormSpacer type="input" />
        {/* todo error is not showing */}
        <Controller
          name="occuredDate"
          control={control}
          render={({ field: { value, ...field }, fieldState: { error } }) => {
            return (
              <DatePicker
                {...field}
                value={dayjs(value as any)}
                id={field.name}
                label="DD/MM/YYYY"
                format="DD/MM/YYYY"
                data-track={field.name}
                data-track-value="mask"
                maxDate={dayjs()}
                data-test={field.name}
                error={!!error}
                helperText={error?.message}
              />
            );
          }}
        />

        <FormGroupLabel
          alignCenter
          required
          legendElementOveride
          title={getTextContent('breach.tell.us.more.breachDetails.dateDiscovered')}
        />
        <FormSpacer type="input" />
        {/* todo error is not showing */}
        <Controller
          name="discoveredDate"
          control={control}
          render={({ field: { value, ...field }, fieldState: { error } }) => {
            return (
              <DatePicker
                {...field}
                value={dayjs(value as any)}
                id={field.name}
                label="DD/MM/YYYY"
                format="DD/MM/YYYY"
                data-track={field.name}
                data-track-value="mask"
                maxDate={dayjs()}
                data-test={field.name}
                error={!!error}
                helperText={error?.message}
              />
            );
          }}
        />

        <FormGroupLabel
          alignCenter
          required={isFieldRequired('breachDescription')}
          legendElementOveride
          title={getTextContent('breach.tell.us.more.breachDetails.breachDescription')}
          subtitle={
            isDataPrivacy
              ? getTextContent('breach.tell.us.more.breachDetails.dataPrivacy.helperText')
              : getTextContent('breach.tell.us.more.breachDetails.helperText')
          }
        />
        <FormSpacer type="input" />
        <TextFieldController
          control={control}
          name="breachDescription"
          label={getTextContent('breach.tell.us.more.breachDetails.breachDescription.label')}
          multiline
          maxLength={4000}
        />

        {isDataPrivacy && (
          <>
            <FormGroupLabel
              required
              title={getTextContent('breach.tell.us.more.breachDetails.dataPrivacy')}
              titleSuffix={
                <InformationPopUp
                  message={getTextContent('breach.tell.us.more.selectAll')}
                  dataTrack="infoIconSelectAll"
                />
              }
            />
            <FormSpacer type="input" />
            <DataPrivacy form={form} />
          </>
        )}

        {isGicop && (
          <>
            <FormGroupLabel
              legendElementOveride
              required={isFieldRequired('gicopArea')}
              title={getTextContent('breach.tell.us.more.breachDetails.gicopArea')}
            />
            <FormSpacer type="input" />

            <DropdownController
              control={control}
              label={getTextContent('breach.tell.us.more.breachDetails.gicopArea.placeholder')}
              name="gicopArea"
              optionList={gicopAreaList.map((value) => {
                return {
                  label: value,
                  value,
                };
              })}
            />
          </>
        )}
        {isTMD && (
          <>
            <FormGroupLabel
              required={isFieldRequired('tmdDescription')}
              alignCenter
              title={getTextContent('breach.tell.us.more.breachDetails.tmd')}
              subtitle={
                (
                  <Box
                    component={'ol'}
                    sx={{
                      listStyleType: 'upper-alpha',
                      li: {
                        textAlign: 'left',
                      },
                    }}
                  >
                    {['one', 'two', 'three', 'four'].map((value, index) => (
                      <li key={index}>{getTextContent(`breach.tell.us.more.breachDetails.tmd.${value}`)}</li>
                    ))}
                  </Box>
                ) as unknown as string
              }
            />

            <TextFieldController
              multiline
              control={control}
              label={getTextContent('breach.tell.us.more.breachDetails.tmd.label')}
              name="tmdDescription"
              maxLength={4000}
            />
          </>
        )}
      </>

      <FormSpacer type="formgroup" />

      {showNextButton && (
        <Button type="button" label={getTextContent('next.label')} disabled={!isValid} onClick={handleClick} />
      )}
    </FormCard>
  );
};

export default BreachDetails;
